import React, { useMemo } from 'react';
import { observer } from 'mobx-react';

import { useStores } from '@/hooks/use-stores';
import { ResponsiveProvider } from '@/hooks/useResponsive';

import { NodeWidgetProps } from './NodeWidget.types';
import { Container } from './NodeWidget.styles';
import DetailsDrawer from './DetailsDrawer/DetailsDrawer';
import { MenuType } from '@/store/PIDEditing/PIDEditStore';
import EditAssetDrawer from './EditAssetDrawer/EditAssetDrawer';
import { ASSET_DEVICE_TYPES } from './EditAssetDrawer/EditAssetDrawer.utils';

const NodeWidget = observer(({ entity, children, isDummy, fromSCC }: NodeWidgetProps) => {
  const { mapBuilderStore, pidEditStore } = useStores();

  const onClickDevice = () => {
    pidEditStore.hideMenu();
    mapBuilderStore.setSelectedDevice(entity);
    mapBuilderStore.setSelectedDeviceTitle(entity.deviceType);
  };

  const isDetailsModalOpen = useMemo<boolean>(
    () => mapBuilderStore.getDetailsModalVisibiltyByDeviceId(entity.deviceId, mapBuilderStore.isDetailsModalMinimized),
    [mapBuilderStore.selectedDevice, mapBuilderStore.isDetailsModalMinimized],
  );

  const isEditAssetModalOpen = useMemo<boolean>(
    () =>
      ASSET_DEVICE_TYPES.includes(entity.deviceType) &&
      pidEditStore.isInEditMode &&
      pidEditStore.activeMenu?.type === MenuType.DeviceDetails &&
      pidEditStore.activeMenu?.context?.entity?.deviceId === entity.deviceId,
    [pidEditStore.isInEditMode, pidEditStore.activeMenu?.type, pidEditStore.activeMenu?.context?.entity?.deviceId],
  );

  const onRightClickDevice = (e: React.MouseEvent) => {
    e.preventDefault();
    if (pidEditStore.isInEditMode) {
      const canvas = document.querySelector('.diagram-container'); // Or a more specific selector
      if (canvas) {
        pidEditStore.showMenu(
          MenuType.SensorActions,
          { x: e.clientX, y: e.clientY },
          { deviceType: entity.deviceType, entity: entity },
        );
      }
    }
  };

  const isPickerOpen = pidEditStore.activeMenu?.type === MenuType.ConnectionPicker;
  const isSelectedForConnection = isPickerOpen && pidEditStore.selectedSourceDeviceIds.includes(entity.deviceId);
  const isTargetDevice = pidEditStore.deviceState.targetDeviceId.includes(entity.deviceId);
  const isAssetConnectionsInEditIds = pidEditStore.assetConnectionsInEditIds?.includes(entity.deviceId);
  const isSelected =
    (isSelectedForConnection || isTargetDevice || isAssetConnectionsInEditIds) && pidEditStore.isEditModeEnabled;
  return (
    <>
      <ResponsiveProvider>
        <DetailsDrawer isDetailsModalOpen={isDetailsModalOpen} entity={entity} fromSCC={fromSCC} />
        <EditAssetDrawer isEditAssetModalOpen={isEditAssetModalOpen} entity={entity} />
      </ResponsiveProvider>
      <Container
        $isDummy={isDummy}
        onClick={isDummy ? null : onClickDevice}
        data-deviceid={entity.deviceId}
        className="node-widget-container"
        onContextMenu={onRightClickDevice}
        $isSelected={isSelected}
      >
        {children}
      </Container>
    </>
  );
});

export default NodeWidget;
